import { CstNode, IToken, ITokenConfig, Lexer, TokenType } from 'chevrotain';

import { AllCstTypes } from './generatedOperationTypes';
import {
  FloatValue,
  IntValue,
  NameButNotKeyword,
  SharedNodeType,
  SharedParser,
  StringValue,
  createToken as createSharedToken,
  sharedKeywordTokens,
  sharedTokens,
} from './parseSharedCST';

// #NameButNotKeyword We manually add the general Identifier (Name) AFTER all the keyword token types.
const allTokens: TokenType[] = [...sharedTokens];

const createToken = (config: ITokenConfig) =>
  createSharedToken(config, allTokens);

allTokens.push(NameButNotKeyword);
const Dollar = createToken({ name: 'Dollar', pattern: '$' });

const GraphQLLexer = new Lexer(allTokens);

type ValueSpecificNodeType = 'Value' | 'Variable';
type ValueNodeType = ValueSpecificNodeType | SharedNodeType;

type CstParserRules = {
  [nodeType in ValueNodeType]: (idx?: number) => CstNode;
};

class ValueParser extends SharedParser<ValueSpecificNodeType> {
  constructor() {
    super(allTokens, sharedKeywordTokens);
  }

  addRulesToParser() {
    // ##AddRulesToParser TODO (jason) could not get this class to have a union
    // index signature, this should be safe though
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $: ValueParser & CstParserRules = this as any;

    // The parsing methods

    // https://spec.graphql.org/draft/#Value
    //   Value[const]:
    //     [If not const] Variable
    //     IntValue
    //     FloatValue
    //     StringValue
    //     BooleanValue
    //     NullValue
    //     EnumValue
    //     ListValueConst [?const]
    //     ObjectValueConst [?const]
    $.RULE('Value', (isConst) => {
      $.OR<CstNode | IToken>([
        { GATE: () => !isConst, ALT: () => $.SUBRULE($.Variable) },
        { ALT: () => $.CONSUME(IntValue) },
        { ALT: () => $.CONSUME(FloatValue) },
        { ALT: () => $.CONSUME(StringValue) },
        { ALT: () => $.SUBRULE($.BooleanValue) },
        { ALT: () => $.SUBRULE($.NullValue) },
        { ALT: () => $.SUBRULE($.EnumValue) },
        { ALT: () => $.SUBRULE($.ListValue, { ARGS: [isConst] }) },
        { ALT: () => $.SUBRULE($.ObjectValue, { ARGS: [isConst] }) },
      ]);
    });

    // https://spec.graphql.org/draft/#Variable
    //   Variable :
    //     $ Name
    $.RULE('Variable', () => {
      $.CONSUME(Dollar);
      $.SUBRULE($.Name);
    });
  }
}

// #AddRulesToParser
const parser = new ValueParser() as ValueParser & CstParserRules;
export function parseValueCST(
  text: string,
  offset: { line: number; column: number; index: number },
) {
  const lexResult = GraphQLLexer.tokenize(text);

  // This parser will be used to parse something from mid
  // operation, so we should add the line / column offset

  lexResult.tokens = lexResult.tokens.map((token) => ({
    ...token,
    startOffset: token.startOffset + offset.index,
    startLine:
      token.startLine === undefined ? undefined : token.startLine + offset.line,
    startColumn:
      token.startLine === 1
        ? token.startColumn === undefined
          ? undefined
          : token.startColumn + offset.column
        : token.startColumn,
    endOffset:
      token.endOffset === undefined
        ? undefined
        : token.endOffset + offset.index,
    endLine:
      token.endLine === undefined ? undefined : token.endLine + offset.line,
    endColumn:
      token.startLine === 1
        ? token.endColumn === undefined
          ? undefined
          : token.endColumn + offset.column
        : token.endColumn,
  }));
  // setting a new input will RESET the parser instance's state.
  parser.input = lexResult.tokens;
  // any top level rule may be used as an entry point
  const cst: AllCstTypes['Value'] = parser.Value() as AllCstTypes['Value'];

  return {
    value: cst,
    lexErrors: lexResult.errors,
    parseErrors: parser.errors,
  };
}
